import { Service } from '@toeverything/infra';
import { nanoid } from 'nanoid';

import { ACCOUNT_TEMPLATE_CONTENT_VERSION } from '../constants';
import { AccountTemplateList } from '../entities/list';
import type { AccountTemplateContentStore } from '../store/content';
import type { AccountTemplateListStore } from '../store/list';
import type {
  AccountTemplateBlob,
  AccountTemplateContent,
  AccountTemplateMeta,
  AccountTemplatePayload,
} from '../type';

type NewTemplateMeta = Omit<
  AccountTemplateMeta,
  'id' | 'createdAt' | 'updatedAt' | 'contentVersion'
>;

/**
 * The account-level ("shared") template library. Lives in the root scope: it is
 * bound to the signed-in user, not to a workspace, and is reachable from every
 * workspace the user opens.
 */
export class AccountTemplateService extends Service {
  readonly list = this.framework.createEntity(AccountTemplateList);

  constructor(
    private readonly listStore: AccountTemplateListStore,
    private readonly contentStore: AccountTemplateContentStore
  ) {
    super();
  }

  async create(
    meta: NewTemplateMeta,
    payload: AccountTemplatePayload,
    blobs: Map<string, AccountTemplateBlob>
  ): Promise<string> {
    const id = nanoid();
    const now = Date.now();

    // content first, so a visible row never points at an empty doc
    await this.contentStore.save(id, payload, blobs);
    this.listStore.upsert({
      ...meta,
      id,
      createdAt: now,
      updatedAt: now,
      contentVersion: ACCOUNT_TEMPLATE_CONTENT_VERSION,
    });
    return id;
  }

  async replaceContent(
    id: string,
    payload: AccountTemplatePayload,
    blobs: Map<string, AccountTemplateBlob>,
    meta?: Partial<NewTemplateMeta>
  ): Promise<void> {
    await this.contentStore.save(id, payload, blobs);
    this.listStore.update(id, {
      ...meta,
      updatedAt: Date.now(),
      contentVersion: ACCOUNT_TEMPLATE_CONTENT_VERSION,
    });
  }

  rename(id: string, name: string) {
    this.listStore.update(id, { name, updatedAt: Date.now() });
  }

  setIcon(id: string, icon?: string) {
    this.listStore.update(id, { icon, updatedAt: Date.now() });
  }

  async remove(id: string) {
    // row first, so a half-deleted template is never listed
    this.listStore.remove(id);
    try {
      await this.contentStore.clear(id);
    } catch {
      // the row is gone, an orphan content doc is harmless
    }
  }

  loadContent(id: string): Promise<AccountTemplateContent | null> {
    return this.contentStore.load(id);
  }
}
